import {useEffect, useRef} from 'react';
import {shallowEqual, useSelector, useStore} from 'react-redux';
import {
  selectCurrentPlayer,
  selectPhase,
  selectRound,
  selectUnits,
  selectWarPhaseStage
} from '../../redux/reducers/sharedStateReducer/selectors';
import {
  selectGoblinsTrapKillNoticeState,
  selectIsFieldCutscenePlaying,
  selectSelectedUnitId
} from '../../redux/reducers/privateStateReducer/selectors';
import {deselectUnit} from '../../redux/actions';
import {REGISTRY_SINGLETON} from '../../scenes/Registry';
import {selectPhaseProgress} from '../../utils/gameState/progress/selectPhaseProgress';
import {checkIfUnitTurnSkipShown} from '../../utils/gameState/progress/unitTurnSkips';
import {
  checkIfUnitAutoSelectionActive,
  checkIfUnitTurnSkipStage
} from '../../utils/unitAutoSelection/checkIfUnitAutoSelectionActive';
import {
  advanceFromSelectedUnit,
  autoSelectNextUnit,
  focusCameraOnSelectedUnit,
  RESULT_HOLDING_STAGES,
  selectNearestUnitBySpace,
  skipSelectedUnitTurn
} from '../../utils/unitAutoSelection/unitAutoSelectionCommands';
import {checkIfHotkeyBelongsToPage} from '../../utils/unitAutoSelection/hotkeyTargets';

// Пауза после конца анимации действия, прежде чем выбрать следующий отряд:
// игрок успевает увидеть, чем закончился ход
export const NEXT_UNIT_PAUSE_MS = 400;
// Как часто проверять, закончилась ли анимация на поле
const ANIMATION_POLL_MS = 100;

const NO_IDS = [];

const selectActingPlayerId = (state) => selectCurrentPlayer(state)?.id || null;
const selectActionableIds = (state) => {
  const playerId = selectActingPlayerId(state);
  return playerId ? selectPhaseProgress(state, playerId).actionableIds : NO_IDS;
};
// Отряды, за которыми следит автовыбор: способные действовать и пропустившие
// ход, пока они не сходили, — пропустившего игрок может выбрать и сходить им
const selectTrackedIds = (state) => {
  const actionableIds = selectActionableIds(state);
  const skippedIds = (selectUnits(state) || [])
    .filter((unit) => checkIfUnitTurnSkipShown(state, unit))
    .map((unit) => unit.id);
  return skippedIds.length ? [...actionableIds, ...skippedIds] : actionableIds;
};
const selectStageKey = (state) => `${selectRound(state)}|${selectPhase(state)}|${selectWarPhaseStage(state)}|${selectActingPlayerId(state)}`;

const checkIfFieldAnimating = () => Boolean(REGISTRY_SINGLETON.getHexGridRenderer()?.isFieldActionAnimating?.());

// Карточка «отряд попал в ловушку гоблинов» на экране ходившего игрока (см.
// GoblinsTrapKillNotice): гаснет сама через несколько секунд или по клику
const checkIfGoblinsTrapNoticeShown = (state) => {
  const {show, blocking, isNoticeVisible} = selectGoblinsTrapKillNoticeState(state) || {};
  return Boolean(show && blocking && isNoticeVisible);
};

// Идет ли на поле то, что игрок должен досмотреть, прежде чем кадр уедет к
// следующему отряду: анимация действия (перемещение, залп), карточка
// срабатывания ловушки Гоблинов после перемещения или заставка поля.
// У кражи Лепреконов и способностей анимации на поле нет — только тост и звук
const checkIfFieldBusy = (state) => checkIfFieldAnimating()
  || checkIfGoblinsTrapNoticeShown(state)
  || selectIsFieldCutscenePlaying(state);

/**
 * Автовыбор отрядов на этапах войны (change unit-auto-selection, design.md,
 * решение 4). Компонент без разметки, смонтирован после EntitiesAutoSelector:
 * тот снимает неподходящий выбор на смене этапа, этот выбирает следующий.
 *
 * Переход к следующему отряду выводится из подсчета дел, а не встраивается в
 * каждое действие: «текущий» отряд пропал из способных действовать — значит,
 * он сходил или пропустил ход. Ручной выбор и ручное снятие выделения не
 * перебиваются. На стрельбе и способностях выбор остается на отряде ради
 * результата в футере — к следующему ведет пробел («дальше»).
 */
const UnitAutoSelector = () => {
  const store = useStore();
  const isActive = useSelector(checkIfUnitAutoSelectionActive);
  const stageKey = useSelector(selectStageKey);
  const warPhaseStage = useSelector(selectWarPhaseStage);
  const selectedUnitId = useSelector(selectSelectedUnitId);
  const trackedIds = useSelector(selectTrackedIds, shallowEqual);

  // Отряд, за которым следит автовыбор: выбранный им или игроком
  const currentUnitIdRef = useRef(null);
  const timerRef = useRef(null);
  // Отряд, после действия которого запланирован выбор следующего: если он
  // снова может действовать (действие отменили в паузе), переход не нужен
  const scheduledFromUnitIdRef = useRef(null);

  const cancelScheduled = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    scheduledFromUnitIdRef.current = null;
  };

  // Выполнить, когда поле освободится (см. checkIfFieldBusy), и после паузы:
  // игрок успевает увидеть, чем закончился ход, а камера не едет посреди
  // полета. Если за паузу на поле снова что-то началось — ждем и его
  const scheduleAfterAnimation = (callback) => {
    cancelScheduled();
    const waitForIdle = () => {
      if (checkIfFieldBusy(store.getState())) {
        timerRef.current = setTimeout(waitForIdle, ANIMATION_POLL_MS);
        return;
      }
      timerRef.current = setTimeout(() => {
        if (checkIfFieldBusy(store.getState())) {
          waitForIdle();
          return;
        }
        timerRef.current = null;
        scheduledFromUnitIdRef.current = null;
        if (checkIfUnitAutoSelectionActive(store.getState())) {
          callback();
        }
      }, NEXT_UNIT_PAUSE_MS);
    };
    waitForIdle();
  };

  // Выбрать следующий. isStillWanted перепроверяет к этому моменту, что игрок
  // ничего не выбрал сам. fromUnitId — отряд, после действия которого идет
  // переход (в начале этапа его нет)
  const scheduleNextUnit = (getOriginHexKey, isStillWanted, fromUnitId = null) => {
    scheduleAfterAnimation(() => {
      if (!isStillWanted()) {
        return;
      }
      const unit = autoSelectNextUnit(store, getOriginHexKey());
      currentUnitIdRef.current = unit?.id || null;
      if (!unit && selectSelectedUnitId(store.getState())) {
        store.dispatch(deselectUnit());
      }
    });
    scheduledFromUnitIdRef.current = fromUnitId;
  };

  useEffect(() => cancelScheduled, []);

  // Начало этапа (или стал свой ход): свой выбранный отряд, которому есть что
  // делать, остается, и кадр подводится к нему; иначе — ближайший к центру
  // свободной области
  useEffect(() => {
    cancelScheduled();
    currentUnitIdRef.current = null;
    if (!isActive) {
      return;
    }
    const startState = store.getState();
    const startUnitId = selectSelectedUnitId(startState);
    if (startUnitId && selectTrackedIds(startState).includes(startUnitId)) {
      currentUnitIdRef.current = startUnitId;
      scheduleAfterAnimation(() => {
        if (selectSelectedUnitId(store.getState()) === startUnitId) {
          focusCameraOnSelectedUnit(store);
        }
      });
      return;
    }
    scheduleNextUnit(
      () => REGISTRY_SINGLETON.getGameScene()?.getFreeAreaCenterHexKey?.() || null,
      () => {
        const state = store.getState();
        const selected = selectSelectedUnitId(state);
        // Пока шла анимация, игрок сам выбрал отряд — его выбор остается
        if (selected && selectTrackedIds(state).includes(selected)) {
          currentUnitIdRef.current = selected;
          return false;
        }
        return !selected || selected === startUnitId;
      }
    );
    // scheduleNextUnit читает состояние сам — пересчет только на смене этапа
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, stageKey]);

  // Выбор и подсчет дел изменились
  useEffect(() => {
    if (!isActive) {
      return;
    }
    const currentUnitId = currentUnitIdRef.current;

    // Действие отряда, после которого ждали паузу перед выбором следующего,
    // отменили (перемещение или манёвр): отряд снова может действовать, и
    // переходить от него не к чему — ни выбор, ни кадр не трогаем
    if (scheduledFromUnitIdRef.current && trackedIds.includes(scheduledFromUnitIdRef.current)) {
      cancelScheduled();
    }

    // Игрок (или команда) выбрал свой отряд, которому есть что делать, в том
    // числе пропустивший ход
    if (selectedUnitId && trackedIds.includes(selectedUnitId)) {
      if (selectedUnitId !== currentUnitId) {
        cancelScheduled();
        currentUnitIdRef.current = selectedUnitId;
      }
      return;
    }
    if (!currentUnitId || trackedIds.includes(currentUnitId)) {
      // Текущий все еще может действовать (или пропустил ход — пропуск сам
      // выбирает следующего, см. skipSelectedUnitTurn), а выделение снято —
      // автовыбор не вмешивается до следующего действия или пропуска
      if (currentUnitId && !selectedUnitId) {
        currentUnitIdRef.current = null;
      }
      return;
    }

    // Текущий сходил. На стрельбе и способностях выбор остается на отряде
    // ради результата в футере, и к следующему ведет только «дальше» (пробел
    // или кнопка в футере). Снял игрок выделение сам — кликом по чужому
    // отряду или ячейке для осмотра — автовыбор не вмешивается, как и при
    // ручном снятии выделения на других этапах
    if (RESULT_HOLDING_STAGES.includes(warPhaseStage)) {
      if (selectedUnitId !== currentUnitId) {
        cancelScheduled();
        currentUnitIdRef.current = null;
      }
      return;
    }
    scheduleNextUnit(
      () => (selectUnits(store.getState()) || []).find((unit) => unit.id === currentUnitId)?.hexKey || null,
      () => {
        const selected = selectSelectedUnitId(store.getState());
        return !selected || selected === currentUnitId;
      },
      currentUnitId
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, selectedUnitId, trackedIds, warPhaseStage]);

  // Пробел: пропустить ход выбранного отряда, а после выстрела или
  // способности — перейти к следующему. Если выделение снято или выбранному
  // отряду делать нечего — выбрать ближайший, который может действовать. Фаза захвата и preventDefault: иначе
  // браузер нажал бы кнопку в фокусе (например, кнопку хода) или прокрутил страницу
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.code !== 'Space' && event.key !== ' ') {
        return;
      }
      if (checkIfHotkeyBelongsToPage(event) || !checkIfUnitTurnSkipStage(store.getState())) {
        return;
      }
      // На этапах с делами отрядов пробел — только команда пропуска: даже если
      // пропускать нечего, он не должен нажать кнопку в фокусе
      event.preventDefault();
      event.stopPropagation();
      if (event.repeat) {
        return;
      }
      if (skipSelectedUnitTurn(store) || advanceFromSelectedUnit(store) || selectNearestUnitBySpace(store)) {
        cancelScheduled();
        currentUnitIdRef.current = selectSelectedUnitId(store.getState());
      }
    };
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};

export default UnitAutoSelector;
